<template>
  <div v-if="pictures.length > 1">
    <div class="photo-viewer-main">
      <Swiper :modules="[Navigation]" :navigation="true" :loop="true" @swiper="onSwiper" @slide-change="onSlideChange">
        <SwiperSlide v-for="(picture, index) in pictures" :key="index">
          <div class="photo-viewer-image-wrap">
            <img :src="getImageUrlToFillSquare(picture.path, picture.width, picture.height, 640)" :alt="picture.alt" :loading="index === 0 ? 'eager' : 'lazy'" @click="isShowModal = true" />
            <div v-if="picture.comment" class="photo-viewer-comment">{{ picture.comment }}</div>
          </div>
        </SwiperSlide>
      </Swiper>
      <div :class="$style.photoPager">{{ `${currentIndex + 1} / ${pictures.length}` }}</div>
    </div>
    <ul v-if="hasThumbnail" ref="thumbRef" class="photo-viewer-thumbnail">
      <li v-for="(picture, index) in pictures" :key="index" :class="index === currentIndex ? 'is-current' : ''" @click="onClickThumbnail(index)">
        <div class="photo">
          <img :src="getImageUrlToFillSquare(picture.path, picture.width, picture.height, 120)" :alt="picture.alt" width="40" height="40" />
        </div>
      </li>
    </ul>
    <IModal v-model="isShowModal" :destroy-on-close="true" :fullscreen="true">
      <PhotoDialog :pictures="pictures" :current-index="currentIndex" :is-show-modal="isShowModal" @change="onChangeDialogSlide" />
    </IModal>
  </div>
  <div v-else>
    <div class="photo-viewer-image-wrap">
      <img :src="getImageUrlToFillSquare(pictures[0].path, pictures[0].width, pictures[0].height, 640)" :alt="pictures[0].alt" @click="isShowModal = true" />
      <div v-if="pictures[0].comment" class="photo-viewer-comment">{{ pictures[0].comment }}</div>
    </div>
    <IModal v-model="isShowModal" :destroy-on-close="true" :fullscreen="true">
      <PhotoDialog :pictures="pictures" :is-show-modal="isShowModal" />
    </IModal>
  </div>
</template>

<script setup lang="ts">
import type { Swiper as SwiperInstance } from 'swiper';
import { Navigation } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/vue';
import { ref } from 'vue';
import { dynamicImageUrl } from '@/js/lib/helper/url';
import IModal from '@/js/components/molecules/IModal.vue';
import PhotoDialog from './PhotoDialog.vue';
import 'swiper/css';
import 'swiper/css/navigation';

defineProps<{
  pictures: {
    path: string;
    alt: string;
    width: number;
    height: number;
    comment: string;
  }[];
  hasThumbnail?: boolean;
}>();

const thumbRef = ref<HTMLUListElement>();
const isShowModal = ref(false);
const swiper = ref<SwiperInstance>();
const currentIndex = ref(0);
const getImageUrlToFillSquare = (path: string, width: number, height: number, squareSize: number) => {
  let w: number | undefined;
  let h: number | undefined;

  if (width > height) {
    h = squareSize;
  } else {
    w = squareSize;
  }

  return dynamicImageUrl(path, { width: w, height: h });
};

const onSwiper = (s: SwiperInstance) => {
  swiper.value = s;
};

const onSlideChange = (s: SwiperInstance) => {
  if (currentIndex.value !== s.realIndex) {
    currentIndex.value = s.realIndex;
    thumbRef.value?.children[currentIndex.value]?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
  }
};

const onClickThumbnail = (index: number) => {
  swiper.value?.slideToLoop(index);
};

const onChangeDialogSlide = (index: number) => {
  swiper.value?.slideToLoop(index);
};
</script>

<style module lang="scss">
.photoPager {
  background-color: var(--color-black-alpha40);
  border-radius: 20px;
  color: var(--color-white);
  font-size: 12px;
  padding: 2px 10px;
  position: absolute;
  right: 10px;
  top: 10px;
  z-index: 1;
}
</style>
